import React, { useState, useRef, useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectCoverflow, Pagination, Mousewheel } from 'swiper/modules';
import { useNavigate, useLocation } from 'react-router-dom';
import BackgroundSlider from './BackgroundSlider';
import HeaderMenu from './HeaderMenu';
import slides from '../Slides';
import ScrollPrompt from './ScrollPrompt';
import { fetchVideoStatistics } from '../services/youtubeService';

import 'swiper/css';
import 'swiper/css/effect-coverflow';
import 'swiper/css/pagination';

import './CoverflowSwiper.css';

const CoverflowSwiper = () => {
    const location = useLocation();
    const initialIndex = location.state?.activeIndex || 0;
    const [activeIndex, setActiveIndex] = useState(initialIndex);
    const [filteredSlides, setFilteredSlides] = useState(slides);
    const [loop, setLoop] = useState(true); // State to control Swiper loop
    const [isModalOpen, setIsModalOpen] = useState(false); // Track if modal is open
    const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
    const swiperRef = useRef(null);
    const navigate = useNavigate();

    // Detect mobile device
    const isMobile = () => {
        return window.innerWidth <= 768;
    };

    const handleSlideChange = (swiper) => {
        setActiveIndex(swiper.realIndex);
    };

    const handlePlayVideo = () => {
        const videoId = filteredSlides[activeIndex]?.videoId;
        if (videoId) {
            navigate(`/video/${videoId}`, { state: { activeIndex } });
        }
    };

    const handleFilter = async (type) => {
        const filtered = type === null
            ? slides
            : slides.filter(slide => slide.type === type);

        const videoIds = filtered.map((s) => s.videoId).filter(Boolean);
        const statsMap = await fetchVideoStatistics(videoIds);

        const enrichedSlides = filtered.map((slide) => ({
            ...slide,
            statistics: statsMap[slide.videoId] || {},
        }));

        setFilteredSlides(enrichedSlides);
        setLoop(type === null);

        // Delay index reset slightly so Swiper + background sync properly
        setTimeout(() => {
            setActiveIndex(0);
            swiperRef.current?.slideTo(0, 0);
        }, 0);
    };

    useEffect(() => {
        const loadViewCounts = async () => {
            const videoIds = slides.map((s) => s.videoId).filter(Boolean);
            const statsMap = await fetchVideoStatistics(videoIds);

            const enrichedSlides = slides.map((slide) => ({
                ...slide,
                statistics: statsMap[slide.videoId] || {},
            }));

            setFilteredSlides(enrichedSlides);
        };

        loadViewCounts();

        const handleGlobalScroll = (event) => {
            if (!isModalOpen && swiperRef.current) {
                if (event.deltaY > 0) {
                    swiperRef.current.slideNext(200);
                } else if (event.deltaY < 0) {
                    swiperRef.current.slidePrev(200);
                }
            }
        };

        const handleArrowNavigation = (event) => {
            if (!isModalOpen && swiperRef.current) {
                if (event.key === 'ArrowRight') {
                    swiperRef.current.slideNext(200);
                } else if (event.key === 'ArrowLeft') {
                    swiperRef.current.slidePrev(200);
                }
            }
        };

        // Only add wheel and keyboard events on desktop
        if (!isMobile()) {
            window.addEventListener('wheel', handleGlobalScroll);
            window.addEventListener('keydown', handleArrowNavigation);
        }

        return () => {
            window.removeEventListener('wheel', handleGlobalScroll);
            window.removeEventListener('keydown', handleArrowNavigation);
        };
    }, [isModalOpen]);

    const currentSlide = filteredSlides[activeIndex] || slides[0];
    const firstBackgroundImage = currentSlide.backgroundImages?.[0];
    const floatingTextMap = {
        'The Carnival': '#2 Most Viewed!',
        'Rebellion of the Apes - First Trailer': '#1 Most Viewed!',
        'Rebellion of the Apes - Final Trailer': 'New - A Must Watch!',
    };

    const floatingText = floatingTextMap[currentSlide.title];

    // Mobile-specific swiper settings
    const getSwiperSettings = () => {
        const baseSettings = {
            effect: "coverflow",
            grabCursor: true,
            centeredSlides: true,
            loop: loop,
            slidesPerView:9,
            speed: 200,
            onSwiper: (swiper) => (swiperRef.current = swiper),
            onSlideChange: handleSlideChange,
            coverflowEffect: {
                rotate: 20,
                stretch: -60,
                depth: 100,
                modifier: 1,
                slideShadows: false,
            },
            pagination: { clickable: true },
            mousewheel: false,
            modules: [EffectCoverflow, Pagination, Mousewheel],
            className: "mySwiperWide",
            initialSlide: activeIndex,
        };

        if (isMobile()) {
            return {
                ...baseSettings,
                slidesPerView: 3, // Fewer slides on mobile
                coverflowEffect: {
                    ...baseSettings.coverflowEffect,
                    rotate: 15, // Less rotation on mobile
                    stretch: -40, // Less stretch on mobile
                    depth: 80, // Less depth on mobile
                },
                // Mobile-specific touch settings
                touchRatio: 1,
                touchAngle: 45,
                simulateTouch: true,
                shortSwipes: true,
                longSwipes: true,
                longSwipesRatio: 0.5,
                longSwipesMs: 300,
                followFinger: true,
                threshold: 0,
                touchMoveStopPropagation: false,
                iOSEdgeSwipeDetection: true,
                iOSEdgeSwipeThreshold: 20,
                touchEventsTarget: 'container',
                resistance: true,
                resistanceRatio: 0.85,
            };
        }

        return {
            ...baseSettings,
            slidesPerView: 9,
        };
    };

    return (
        <div className="swiper-page-container">
            <ScrollPrompt />
            <HeaderMenu
                onFilter={handleFilter}
                isModalOpen={isModalOpen} // Pass modal state
                setIsModalOpen={setIsModalOpen} // Pass modal updater
            />

            <BackgroundSlider
                images={currentSlide.backgroundImages || []}
                interval={4000}
            />

            {/* modal */}
            {isInfoModalOpen && (
                <div className="modal-overlay" onClick={() => setIsInfoModalOpen(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <img
                            src={firstBackgroundImage}
                            alt={currentSlide.title}
                            className="modal-image"
                        />
                        <div className="modal-text">
                            <div className="modal-header">
                                <h2 className="modal-title">{currentSlide.title}</h2>
                                <span className="modal-subtext">{currentSlide.year}</span>
                            </div>
                            <p>{currentSlide.description || 'No description available.'}</p>

                            <div className="modal-buttons">
                                <button
                                    className="play-button"
                                    onClick={() => {
                                        setIsInfoModalOpen(false);
                                        handlePlayVideo();
                                    }}
                                >
                                    ▶ Play
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {floatingText && (
                <div className="floating-text-right">{floatingText}</div>
            )}

            <div className="title-image-section">
                {currentSlide.titleImage && (
                    <img
                        src={currentSlide.titleImage}
                        alt={currentSlide.title}
                        className="title-image"
                    />
                )}

                <div className="genres">
                    {(currentSlide.genre || []).join(' · ')}
                </div>

                <div className="button-container">
                    <button className="play-button" onClick={handlePlayVideo}>
                        ▶ Play
                    </button>
                    <button className="more-info-button" onClick={() => setIsInfoModalOpen(true)}>
                        Info
                    </button>
                    {currentSlide.statistics?.viewCount && (
                        <p className="slide-description">
                            {Number(currentSlide.statistics.viewCount).toLocaleString()} views
                        </p>
                    )}
                </div>
            </div>

            <Swiper {...getSwiperSettings()}>
                {filteredSlides.map((slide, index) => (
                    <SwiperSlide key={index}>
                        <div
                            className={`thumbnail-container ${activeIndex === index ? 'active-thumbnail' : ''}`}
                            onClick={() => swiperRef.current.slideToLoop(index, 500)}
                        >
                            <img src={slide.carouselImage} alt={`Slide ${index + 1}`} className="thumbnail-image" />
                        </div>
                    </SwiperSlide>
                ))}
            </Swiper>
        </div>
    );
};

export default CoverflowSwiper;
